import { AUTH_EXPIRED_EVENT, ApiError } from "./client";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

export interface Pagination {
  page: number;
  page_size: number;
  total: number;
  pages: number;
  has_previous: boolean;
  has_next: boolean;
}

export interface AdminUser {
  id: number;
  username: string;
  role: string;
  status: string;
  credit_balance: number;
  task_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminTask {
  id: number;
  user_id: number;
  status: string;
  total_count: number;
  success_count: number;
  failed_count: number;
  charged_count: number;
  concurrency: number;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface AdminTaskItem {
  id: number;
  line_no: number;
  account_id?: number | null;
  status: string;
  stage: string;
  error_code: string;
  error_message: string;
  charged: boolean;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface AdminCode {
  id: number;
  display_code: string;
  points: number;
  status: string;
  used_by?: number | null;
  used_at?: string | null;
  created_by?: number | null;
  created_at: string;
  expires_at?: string | null;
}

export interface LedgerEntry {
  id: number;
  type: string;
  amount: number;
  balance_after: number;
  remark: string;
  task_id?: number | null;
  item_id?: number | null;
  created_at: string;
}

export interface QueueWorker {
  name: string;
  state: string;
  current_job_id?: string | null;
  last_heartbeat?: string | null;
  birth_date?: string | null;
  successful_jobs: number;
  failed_jobs: number;
}

export interface QueueSnapshot {
  status: string;
  queue: string;
  queued?: number | null;
  started?: number;
  failed?: number;
  scheduled?: number;
  deferred?: number;
  worker_count: number;
  busy_workers: number;
  workers: QueueWorker[];
  error?: string;
}

export interface AdminOverview {
  generated_at: string;
  metrics: Record<string, number>;
  queue: QueueSnapshot;
  recent_tasks: AdminTask[];
}

export interface ConsistencyCheck {
  code: string;
  severity: "critical" | "warning" | string;
  count: number;
  ok: boolean;
  description: string;
}

export interface AdminAuditEntry {
  id: number;
  actor_user_id: number;
  action: string;
  resource_type: string;
  resource_ids: string[];
  reason: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

function csrfToken() {
  const entry = document.cookie
    .split("; ")
    .find((item) => item.startsWith("oauth_new_csrf=") || item.startsWith("oauth_csrf="));
  return entry ? decodeURIComponent(entry.slice(entry.indexOf("=") + 1)) : "";
}

function messageOf(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== "object") return fallback;
  const detail = (payload as Record<string, unknown>).detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const message = (detail as Record<string, unknown>).message;
    return typeof message === "string" ? message : JSON.stringify(detail);
  }
  return fallback;
}

async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (method !== "GET" && method !== "HEAD") headers.set("X-CSRF-Token", csrfToken());
  const response = await fetch(`${API_BASE}${path}`, { ...init, method, headers, credentials: "include" });
  const text = await response.text();
  let payload: unknown = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = { detail: text };
  }
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    throw new ApiError(messageOf(payload, `请求失败（${response.status}）`), response.status, payload);
  }
  return payload as T;
}

function query(params: Record<string, string | number | undefined>) {
  const value = new URLSearchParams();
  Object.entries(params).forEach(([key, item]) => {
    if (item !== undefined && item !== "") value.set(key, String(item));
  });
  const encoded = value.toString();
  return encoded ? `?${encoded}` : "";
}

export const adminApi = {
  overview: () => adminRequest<AdminOverview>("/api/v2/admin/overview"),
  health: () => adminRequest<{ status: string; checked_at: string; database: { status: string }; queue: QueueSnapshot }>("/api/v2/admin/health"),
  queue: () => adminRequest<QueueSnapshot>("/api/v2/admin/queue"),
  audit: (page = 1) => adminRequest<{ items: AdminAuditEntry[]; pagination: Pagination }>(`/api/v2/admin/audit${query({ page, page_size: 10 })}`),
  consistency: (staleMinutes = 15) =>
    adminRequest<{ checked_at: string; read_only: boolean; status: string; checks: ConsistencyCheck[] }>(
      `/api/v2/admin/consistency${query({ stale_minutes: staleMinutes })}`,
    ),
  users: (params: { page: number; pageSize: number; q?: string; status?: string; role?: string }) =>
    adminRequest<{ items: AdminUser[]; pagination: Pagination }>(
      `/api/v2/admin/users${query({ page: params.page, page_size: params.pageSize, q: params.q, status: params.status, role: params.role })}`,
    ),
  setUsersStatus: (userIds: number[], status: "active" | "disabled", reason: string) =>
    adminRequest<{ status: string; changed_ids: number[]; unchanged_count: number }>("/api/v2/admin/users/status", {
      method: "POST",
      body: JSON.stringify({ user_ids: userIds, status, reason }),
    }),
  adjustCredits: (userId: number, amount: number, reason: string) =>
    adminRequest<{ status: string; user_id: number; balance: number; ledger_id: number }>(`/api/v2/admin/users/${userId}/credits`, {
      method: "POST",
      body: JSON.stringify({ amount, reason }),
    }),
  ledger: (userId: number, page = 1) =>
    adminRequest<{ items: LedgerEntry[]; pagination: Pagination }>(`/api/v2/admin/users/${userId}/ledger${query({ page, page_size: 25 })}`),
  codes: (params: { page: number; q?: string; status?: string }) =>
    adminRequest<{ items: AdminCode[]; pagination: Pagination }>(
      `/api/v2/admin/redeem-codes${query({ page: params.page, page_size: 25, q: params.q, status: params.status })}`,
    ),
  createCodes: (points: number, count: number, expiresAt: string | null, reason: string) =>
    adminRequest<{ status: string; codes: string[]; count: number }>("/api/v2/admin/redeem-codes", {
      method: "POST",
      body: JSON.stringify({ points, count, expires_at: expiresAt || null, reason }),
    }),
  setCodesStatus: (codeIds: number[], status: "unused" | "disabled", reason: string) =>
    adminRequest<{ status: string; changed_ids: number[] }>("/api/v2/admin/redeem-codes/status", {
      method: "POST",
      body: JSON.stringify({ code_ids: codeIds, status, reason }),
    }),
  tasks: (params: { page: number; status?: string; userId?: number }) =>
    adminRequest<{ items: AdminTask[]; pagination: Pagination }>(
      `/api/v2/admin/tasks${query({ page: params.page, page_size: 25, status: params.status, user_id: params.userId })}`,
    ),
  task: (taskId: number, page = 1) =>
    adminRequest<{ task: AdminTask; items: AdminTaskItem[]; pagination: Pagination }>(
      `/api/v2/admin/tasks/${taskId}${query({ page, page_size: 200 })}`,
    ),
  cancelTasks: (taskIds: number[], reason: string) =>
    adminRequest<{ status: string; canceled_ids: number[]; already_terminal_ids: number[] }>("/api/v2/admin/tasks/cancel", {
      method: "POST",
      body: JSON.stringify({ task_ids: taskIds, reason }),
    }),
};
